import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LayoutGrid, Kanban, Car, Plus, RefreshCw, DollarSign, Calendar,
  Gauge, Clock, AlertTriangle, Package, ChevronRight, BookOpen, Trash2, Droplet,
} from "lucide-react";
import MaintenanceBoard from "@/components/admin/MaintenanceBoard";
import WorkOrderDialog from "@/components/admin/WorkOrderDialog";
import VehicleMaintProfile from "@/components/admin/VehicleMaintProfile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH_DOT = { green: "bg-emerald-400", yellow: "bg-amber-400", red: "bg-red-400" };
const SEV = { overdue: "text-red-300", soon: "text-orange-300", watch: "text-amber-300", ready: "text-sky-300" };
const SEV_LABEL = { overdue: "🔴 OVERDUE", soon: "🟠 DUE NOW", watch: "🟡 DUE SOON", ready: "READY" };

export default function FleetMaintenance({ token, onAuthError }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [view, setView] = useState("dashboard"); // dashboard | board | vehicles | profile | library
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [services, setServices] = useState([]);
  const [profileVehicle, setProfileVehicle] = useState(null);
  const [woDialog, setWoDialog] = useState(null); // { workOrder, defaultVehicleId } | null
  const [refreshKey, setRefreshKey] = useState(0);
  const [newSvc, setNewSvc] = useState({ name: "", default_interval_miles: 0, default_interval_months: 0 });
  const [oil, setOil] = useState({ oil_change_interval_miles: 5000, oil_reminder_enabled: true });

  const loadSettings = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/fleet/settings`, auth); setOil(data); } catch { /* noop */ }
    /* eslint-disable-next-line */
  }, [token]);

  const saveOil = async (patch) => {
    setOil((o) => ({ ...o, ...patch }));
    try { await axios.patch(`${API}/fleet/settings`, patch, auth); toast.success("Oil reminder settings saved"); refresh(); }
    catch { toast.error("Could not save"); }
  };

  const completeFromAlert = async (a) => {
    const svc = a.type === "oil" ? "Oil Change" : a.type === "tires" ? "Tires" : (a.title || "").replace(/ (overdue|due soon|due now)$/i, "");
    const odo = window.prompt(`Complete "${svc}" for ${a.vehicle}\n\nCurrent mileage:`, a.odometer || "");
    if (odo === null || !Number(odo)) { if (odo !== null) toast.error("Mileage is required."); return; }
    const cost = window.prompt("Cost (optional, $):", "") ?? "";
    const notes = window.prompt("Notes (optional):", "") ?? "";
    try {
      const srcId = /^svc_(mi|dt):(.+)$/.exec(a.key || "")?.[2] || "";
      const { data } = await axios.post(`${API}/fleet/complete-service`, {
        vehicle_id: a.vehicle_id, service_type: svc, odometer: Number(odo),
        cost: Number(cost) || 0, notes, source_wo_id: srcId,
      }, auth);
      toast.success(`${svc} completed${data.next_due_mileage ? ` — next due at ${data.next_due_mileage.toLocaleString()} mi` : ""}${data.next_due_date ? ` / ${data.next_due_date}` : ""}. Saved to vehicle history.`);
      loadDash();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete service."); }
  };

  const loadDash = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/fleet/maintenance-dashboard`, auth);
      setDash(data);
    } catch (e) {
      if (e?.response?.status === 401 && onAuthError) onAuthError();
      else toast.error("Failed to load maintenance dashboard");
    } finally {
      setLoading(false);
    }
    /* eslint-disable-next-line */
  }, [token]);

  const loadMeta = useCallback(async () => {
    try {
      const [v, s] = await Promise.all([
        axios.get(`${API}/vehicles?all=true`, auth),
        axios.get(`${API}/service-library`, auth),
      ]);
      setVehicles(v.data || []);
      setServices(s.data || []);
    } catch { /* noop */ }
    /* eslint-disable-next-line */
  }, [token]);

  useEffect(() => { loadDash(); loadMeta(); loadSettings(); /* eslint-disable-next-line */ }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const openProfile = (vid) => { setProfileVehicle(vid); setView("profile"); };
  const openNewWO = (vid) => setWoDialog({ workOrder: null, defaultVehicleId: vid || "" });
  const openEditWO = (wo) => setWoDialog({ workOrder: wo, defaultVehicleId: wo.vehicle_id });

  const openCardById = async (id) => {
    try {
      const { data } = await axios.get(`${API}/work-orders/${id}`, auth);
      openEditWO(data);
    } catch { toast.error("Failed to open work order"); }
  };

  const onWoSaved = () => { setWoDialog(null); refresh(); };

  const addService = async () => {
    if (!newSvc.name.trim()) { toast.error("Enter a service name"); return; }
    try {
      await axios.post(`${API}/service-library`, {
        name: newSvc.name.trim(),
        default_interval_miles: Number(newSvc.default_interval_miles) || 0,
        default_interval_months: Number(newSvc.default_interval_months) || 0,
      }, auth);
      toast.success("Service added");
      setNewSvc({ name: "", default_interval_miles: 0, default_interval_months: 0 });
      loadMeta();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add service"); }
  };
  const delService = async (id) => {
    try { await axios.delete(`${API}/service-library/${id}`, auth); loadMeta(); toast.success("Removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const TABS = [
    ["dashboard", "Dashboard", LayoutGrid], ["board", "Board", Kanban],
    ["vehicles", "Vehicles", Car], ["library", "Service Library", BookOpen],
  ];

  return (
    <div className="space-y-5" data-testid="fleet-maintenance">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Fleet Maintenance</h2>
          <p className="text-white/50 text-sm">Work orders, parts, scheduling, costs & full vehicle history — all in one place.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="fm-refresh-btn" onClick={refresh} className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#0C0C0C] border border-white/10 text-white/80 text-sm font-semibold"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh</button>
          <button data-testid="fm-new-wo-btn" onClick={() => openNewWO("")} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold"><Plus size={15} /> New Work Order</button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 flex-wrap">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} data-testid={`fm-tab-${key}`} onClick={() => setView(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${view === key ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* DASHBOARD */}
      {view === "dashboard" && dash && (
        <div className="space-y-5">
          {/* Automatic oil-change reminder — applies to the whole fleet */}
          <div className="rounded-xl bg-[#0C0C0C] border border-[#F2B705]/25 p-4" data-testid="oil-reminder-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#F2B705]/15 text-[#F2B705]"><Droplet size={18} /></div>
                <div>
                  <div className="text-white font-semibold">Automatic Oil-Change Reminder</div>
                  <div className="text-white/50 text-sm">Applies to every vehicle — current & future. You'll get alerted at each interval.</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-white/60 text-sm">Every</label>
                <input type="number" step="500" data-testid="oil-interval-input"
                  className="w-28 px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]"
                  value={oil.oil_change_interval_miles}
                  onChange={(e) => setOil((o) => ({ ...o, oil_change_interval_miles: e.target.value }))}
                  onBlur={(e) => saveOil({ oil_change_interval_miles: Number(e.target.value) || 5000 })} />
                <span className="text-white/60 text-sm">miles</span>
                <button data-testid="oil-toggle-btn" onClick={() => saveOil({ oil_reminder_enabled: !oil.oil_reminder_enabled })}
                  className={`text-xs px-3 py-2 rounded-md font-semibold border ${oil.oil_reminder_enabled ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-white/50 border-white/10"}`}>
                  {oil.oil_reminder_enabled ? "ON" : "OFF"}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi icon={DollarSign} label="Total Maint. Cost" value={`$${dash.kpis.total_maintenance_cost.toLocaleString()}`} />
            <Kpi icon={Calendar} label="This Month" value={`$${dash.kpis.cost_this_month.toLocaleString()}`} />
            <Kpi icon={Calendar} label="This Year" value={`$${dash.kpis.cost_this_year.toLocaleString()}`} />
            <Kpi icon={Gauge} label="Cost / Mile" value={`$${dash.kpis.fleet_cost_per_mile}`} />
            <Kpi icon={Clock} label="Downtime (days)" value={dash.kpis.downtime_days} />
            <Kpi icon={Package} label="Open Work Orders" value={dash.kpis.open_work_orders} />
            <Kpi icon={AlertTriangle} label="Overdue Alerts" value={dash.kpis.alerts_overdue} accent="text-red-400" />
            <Kpi icon={AlertTriangle} label="Due Soon" value={dash.kpis.alerts_soon} accent="text-amber-400" />
          </div>

          {/* Health summary */}
          <div className="flex gap-3 flex-wrap">
            <HealthPill color="bg-emerald-400" label="Good" count={dash.kpis.vehicles_green} />
            <HealthPill color="bg-amber-400" label="Due Soon" count={dash.kpis.vehicles_yellow} />
            <HealthPill color="bg-red-400" label="Needs Attention" count={dash.kpis.vehicles_red} />
            <HealthPill color="bg-sky-400" label="Parts Ready" count={dash.kpis.parts_ready} />
          </div>

          {/* Alerts */}
          {dash.alerts.length > 0 && (
            <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><AlertTriangle size={15} className="text-amber-400" /> Maintenance Alerts</h3>
              <div className="divide-y divide-white/5" data-testid="fm-alerts-list">
                {dash.alerts.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-2 gap-3">
                    <div className="min-w-0"><div className="text-white/85 text-sm font-semibold truncate">{a.title} <span className="text-white/40 font-normal">· {a.vehicle}</span></div><div className="text-white/40 text-xs truncate">{a.detail}</div></div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[11px] font-bold uppercase ${SEV[a.severity]}`}>{SEV_LABEL[a.severity] || a.severity}</span>
                      {["service", "oil", "tires"].includes(a.type) && (
                        <button data-testid={`fm-complete-${i}`} onClick={() => completeFromAlert(a)}
                          className="text-[10px] font-bold border border-emerald-500/50 text-emerald-300 px-2 py-1 rounded hover:bg-emerald-400 hover:text-black">✓ Complete Service</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fleet health table */}
          <div className="rounded-xl bg-[#0C0C0C] border border-white/10 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 text-white font-semibold">Fleet Health</div>
            <div className="divide-y divide-white/5" data-testid="fm-vehicle-list">
              {dash.vehicles.map((v) => (
                <button key={v.id} onClick={() => openProfile(v.id)} data-testid={`fm-vehicle-${v.id}`}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-left">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${HEALTH_DOT[v.health]}`} />
                    <div className="min-w-0"><div className="text-white text-sm font-semibold truncate">{v.vehicle}</div><div className="text-white/40 text-xs">{v.odometer.toLocaleString()} mi · {v.open_work_orders} open · {v.alerts} alert(s)</div></div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-white/70 text-sm">${v.total_cost.toLocaleString()}</span>
                    <ChevronRight size={16} className="text-white/30" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* BOARD */}
      {view === "board" && <MaintenanceBoard token={token} onOpenCard={openCardById} refreshKey={refreshKey} />}

      {/* VEHICLES */}
      {view === "vehicles" && dash && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fm-vehicle-cards">
          {dash.vehicles.map((v) => (
            <button key={v.id} onClick={() => openProfile(v.id)} className="text-left rounded-xl bg-[#0C0C0C] border border-white/10 p-4 hover:border-white/25" data-testid={`fm-vehicle-card-${v.id}`}>
              <div className="flex items-center justify-between">
                <span className={`w-2.5 h-2.5 rounded-full ${HEALTH_DOT[v.health]}`} />
                <span className="text-[#F2B705] text-sm font-bold">${v.total_cost.toLocaleString()}</span>
              </div>
              <div className="text-white font-semibold mt-2">{v.vehicle}</div>
              <div className="text-white/40 text-xs mt-1">{v.odometer.toLocaleString()} mi · ${v.cost_per_mile}/mi</div>
              <div className="text-white/50 text-xs mt-2">{v.open_work_orders} open · {v.alerts} alert(s)</div>
            </button>
          ))}
        </div>
      )}

      {/* SERVICE LIBRARY */}
      {view === "library" && (
        <div className="space-y-4">
          <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4">
            <h3 className="text-white font-semibold mb-3">Add Custom Service</h3>
            <div className="grid sm:grid-cols-4 gap-2">
              <input className="sm:col-span-2 px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]" placeholder="Service name" value={newSvc.name} onChange={(e) => setNewSvc((s) => ({ ...s, name: e.target.value }))} data-testid="svc-name-input" />
              <input type="number" className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]" placeholder="Every X mi" value={newSvc.default_interval_miles} onChange={(e) => setNewSvc((s) => ({ ...s, default_interval_miles: e.target.value }))} />
              <input type="number" className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]" placeholder="Every X mo" value={newSvc.default_interval_months} onChange={(e) => setNewSvc((s) => ({ ...s, default_interval_months: e.target.value }))} />
            </div>
            <button data-testid="svc-add-btn" onClick={addService} className="mt-3 flex items-center gap-2 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold"><Plus size={15} /> Add Service</button>
          </div>
          <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4">
            <h3 className="text-white font-semibold mb-3">Service Library ({services.length})</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="svc-library-list">
              {services.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-black/40 border border-white/10">
                  <div><div className="text-white text-sm">{s.name}</div><div className="text-white/40 text-xs">{s.default_interval_miles ? `${s.default_interval_miles.toLocaleString()} mi` : ""}{s.default_interval_miles && s.default_interval_months ? " / " : ""}{s.default_interval_months ? `${s.default_interval_months} mo` : ""}{!s.default_interval_miles && !s.default_interval_months ? "as needed" : ""}</div></div>
                  {s.custom && <button onClick={() => delService(s.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PROFILE */}
      {view === "profile" && profileVehicle && (
        <VehicleMaintProfile token={token} vehicleId={profileVehicle} refreshKey={refreshKey}
          onBack={() => setView("dashboard")} onNewWorkOrder={openNewWO} onEditWorkOrder={openEditWO} />
      )}

      {loading && !dash && <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="animate-spin" size={18} /> Loading…</div>}

      {woDialog && (
        <WorkOrderDialog token={token} workOrder={woDialog.workOrder} vehicles={vehicles} services={services}
          defaultVehicleId={woDialog.defaultVehicleId} onClose={() => setWoDialog(null)} onSaved={onWoSaved} />
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4">
      <div className="flex items-center gap-2 text-white/40 text-xs"><Icon size={14} /> {label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}
function HealthPill({ color, label, count }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0C0C0C] border border-white/10">
      <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
      <span className="text-white/70 text-sm">{label}</span>
      <span className="text-white font-bold">{count}</span>
    </div>
  );
}
